/**
 * Geocoding utility using Maps.co API
 * Provides forward geocoding (address to coordinates) with rate limiting
 * 
 * Features:
 * - Multiple query format strategies for maximum accuracy
 * - Reverse geocoding validation to ensure results match expected city/state
 * - Automatic fallback to city-level coordinates if address geocoding fails
 * - Rate limiting to prevent API abuse
 * 
 * Usage:
 * - AI enrichment flow: Automatically geocodes addresses during camp creation/enrichment
 * - Camp edit flow: Can be used to fetch/update coordinates when address is edited
 * 
 * Example:
 * ```typescript
 * const result = await geocodeAddress(
 *   '300 Davis Park Rd',  // Street address
 *   'Ponte Vedra',        // City
 *   'Florida',            // State (full name preferred)
 *   '32081'              // Zip code
 * );
 * // Returns: { latitude: 30.123, longitude: -81.456, accuracy: 'exact', source: 'maps_co' }
 * ```
 */

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  accuracy: 'exact' | 'city' | 'state' | 'failed';
  source: 'maps_co' | 'fallback';
}

interface MapsCoResponse {
  lat: string;
  lon: string;
  display_name: string;
  address?: {
    city?: string;
    state?: string;
    country?: string;
  };
}

// Rate limiting: 1 second delay between requests
let lastRequestTime = 0;
const RATE_LIMIT_DELAY = 1000; // 1 second

/**
 * Get coordinates for an address using Maps.co API
 * @param address - Full address string
 * @param city - City name
 * @param state - State name  
 * @param zipCode - ZIP code
 * @returns Promise<GeocodingResult>
 */
export async function geocodeAddress(
  address?: string,
  city?: string,
  state?: string,
  zipCode?: string
): Promise<GeocodingResult> {
  // Rate limiting
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < RATE_LIMIT_DELAY) {
    await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY - timeSinceLastRequest));
  }
  lastRequestTime = Date.now();

  const MAPS_CO_API_KEY = import.meta.env.VITE_MAPS_CO_API_KEY;
  
  if (!MAPS_CO_API_KEY) {
    console.warn('Maps.co API key not configured - using fallback coordinates');
    return getFallbackCoordinates(city, state);
  }

  try {
    // Strategy 1: If we have zip code, prioritize it (zip codes are very specific)
    // Try multiple query formats to maximize accuracy
    if (address && city && state && zipCode) {
      // Format 1: Full address with zip (most specific)
      const query1 = `${address}, ${city}, ${state} ${zipCode}`;
      console.log(`[GEOCODING] Trying format 1 (full with zip): "${query1}"`);
      const result1 = await geocodeFreeForm(query1, MAPS_CO_API_KEY, city, state);
      if (result1.accuracy !== 'failed' && result1.latitude !== 0 && result1.longitude !== 0) {
        console.log(`[GEOCODING] ✅ Format 1 succeeded`);
        return result1;
      }
      console.warn(`[GEOCODING] ⚠️ Format 1 failed or returned invalid location`);

      // Format 2: Address + zip code only (zip codes are very specific)
      const query2 = `${address} ${zipCode}`;
      console.log(`[GEOCODING] Trying format 2 (address + zip): "${query2}"`);
      const result2 = await geocodeFreeForm(query2, MAPS_CO_API_KEY, city, state);
      if (result2.accuracy !== 'failed' && result2.latitude !== 0 && result2.longitude !== 0) {
        console.log(`[GEOCODING] ✅ Format 2 succeeded`);
        return result2;
      }
      console.warn(`[GEOCODING] ⚠️ Format 2 failed or returned invalid location`);

      // Format 3: Zip code only (zip codes are very specific and should return the right city)
      const query3 = `${zipCode}`;
      console.log(`[GEOCODING] Trying format 3 (zip only): "${query3}"`);
      const result3 = await geocodeFreeForm(query3, MAPS_CO_API_KEY, city, state);
      if (result3.accuracy !== 'failed' && result3.latitude !== 0 && result3.longitude !== 0) {
        console.log(`[GEOCODING] ✅ Format 3 succeeded`);
        return result3;
      }
      console.warn(`[GEOCODING] ⚠️ Format 3 failed or returned invalid location`);

      // Format 4: City, State, Zip (zip codes are very specific)
      const query4 = `${city}, ${state} ${zipCode}`;
      console.log(`[GEOCODING] Trying format 4 (city + zip): "${query4}"`);
      const result4 = await geocodeFreeForm(query4, MAPS_CO_API_KEY, city, state);
      if (result4.accuracy !== 'failed' && result4.latitude !== 0 && result4.longitude !== 0) {
        console.log(`[GEOCODING] ✅ Format 4 succeeded`);
        return result4;
      }
      console.warn(`[GEOCODING] ⚠️ Format 4 failed or returned invalid location`);
    }

    // Try structured geocoding (more accurate when it works)
    if (address && city && state) {
      console.log(`[GEOCODING] Trying structured geocoding: street="${address}", city="${city}", state="${state}", zip="${zipCode || 'none'}"`);
      const structuredResult = await geocodeStructured(address, city, state, zipCode, MAPS_CO_API_KEY);
      if (structuredResult.accuracy !== 'failed' && structuredResult.latitude !== 0 && structuredResult.longitude !== 0) {
        // Validate the result
        const isValid = await validateGeocodeResult(structuredResult, city, state);
        if (isValid) {
          console.log(`[GEOCODING] ✅ Structured geocoding succeeded with validated result`);
          return structuredResult;
        } else {
          console.warn(`[GEOCODING] ⚠️ Structured result didn't match expected city/state, trying free-form...`);
        }
      }
    }

    // Fallback to free-form search with available components
    const searchQuery = buildSearchQuery(address, city, state, zipCode);
    if (searchQuery) {
      console.log(`[GEOCODING] Trying free-form fallback: "${searchQuery}"`);
      const freeFormResult = await geocodeFreeForm(searchQuery, MAPS_CO_API_KEY, city, state);
      if (freeFormResult.accuracy !== 'failed' && freeFormResult.latitude !== 0 && freeFormResult.longitude !== 0) {
        console.log(`[GEOCODING] ✅ Free-form fallback succeeded`);
        return freeFormResult;
      }
    }

    // Final fallback to city-level coordinates
    console.warn(`[GEOCODING] ⚠️ All geocoding attempts failed, using city-level fallback`);
    return getFallbackCoordinates(city, state);

  } catch (error) {
    console.warn('Geocoding failed:', error);
    return getFallbackCoordinates(city, state);
  }
}

/**
 * Validate that geocoded result matches expected city/state
 * Uses reverse geocoding to check the actual location
 */
async function validateGeocodeResult(
  result: GeocodingResult,
  expectedCity: string | undefined,
  expectedState: string | undefined
): Promise<boolean> {
  // If we don't have expected city/state, skip validation
  if (!expectedCity || !expectedState) {
    return true;
  }

  try {
    const MAPS_CO_API_KEY = import.meta.env.VITE_MAPS_CO_API_KEY;
    if (!MAPS_CO_API_KEY) {
      // Can't validate without API key, assume valid
      return true;
    }

    // Reverse geocode to get the actual location
    const reverseParams = new URLSearchParams({
      lat: result.latitude.toString(),
      lon: result.longitude.toString(),
      api_key: MAPS_CO_API_KEY
    });

    const response = await fetch(`https://geocode.maps.co/reverse?${reverseParams}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      console.warn(`[GEOCODING] Reverse geocoding failed: ${response.status}`);
      return true; // Assume valid if we can't validate
    }

    const data: any = await response.json();
    
    // Extract city and state from reverse geocoding response
    // Maps.co reverse geocoding returns address components in different formats
    let returnedCity = '';
    let returnedState = '';
    
    if (data.address) {
      returnedCity = (data.address.city || data.address.town || data.address.village || data.address.municipality || '').toLowerCase();
      returnedState = (data.address.state || data.address.region || '').toLowerCase();
    }
    
    // Also check display_name as fallback
    if (!returnedCity && data.display_name) {
      const displayName = data.display_name.toLowerCase();
      // Try to extract city from display name (format: "City, State, Country")
      const parts = displayName.split(',');
      if (parts.length >= 2) {
        returnedCity = parts[0].trim();
        returnedState = parts[1].trim();
      }
    }
    
    const expectedCityLower = expectedCity.toLowerCase();
    const expectedStateLower = expectedState.toLowerCase();

    // Normalize city names (remove common suffixes, handle variations)
    const normalizeCityName = (cityName: string): string => {
      return cityName
        .replace(/\s+/g, ' ')
        .replace(/\b(beach|bch|city|cty|town|tn|village|vil)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const normalizedExpectedCity = normalizeCityName(expectedCityLower);
    const normalizedReturnedCity = normalizeCityName(returnedCity);

    // Check state first (most important - must match)
    const stateMatches = returnedState.includes(expectedStateLower) || 
                        expectedStateLower.includes(returnedState) ||
                        // Handle abbreviations: "florida" -> "fl" matches "florida"
                        (expectedStateLower.length > 2 && returnedState.length === 2 && 
                         getStateAbbreviation(expectedStateLower) === returnedState.toLowerCase()) ||
                        // Handle full name: "fl" -> "florida" matches "florida"
                        (expectedStateLower.length === 2 && returnedState.length > 2 && 
                         getStateAbbreviation(expectedStateLower) === returnedState.substring(0, 2).toLowerCase());

    if (!stateMatches) {
      console.warn(`[GEOCODING] ❌ State mismatch: Expected "${expectedState}" but got "${returnedState}"`);
      return false;
    }

    // Check city (must match if we have a returned city)
    // If no city was returned, we can't validate it, so accept it
    if (!returnedCity) {
      console.log(`[GEOCODING] ⚠️ No city in reverse geocoding result, accepting based on state match`);
      return true;
    }

    // City must match (strict check)
    const cityMatches = 
      // Exact match (normalized)
      normalizedReturnedCity === normalizedExpectedCity ||
      // Partial match (one contains the other)
      normalizedReturnedCity.includes(normalizedExpectedCity) || 
      normalizedExpectedCity.includes(normalizedReturnedCity) ||
      // Handle variations like "Ponte Vedra Beach" vs "Ponte Vedra"
      // Check if the core words match
      normalizedExpectedCity.split(/\s+/).every(word => 
        word.length > 2 && normalizedReturnedCity.includes(word)
      ) ||
      normalizedReturnedCity.split(/\s+/).every(word => 
        word.length > 2 && normalizedExpectedCity.includes(word)
      );

    if (!cityMatches) {
      console.warn(`[GEOCODING] ❌ City mismatch: Expected "${expectedCity}" (normalized: "${normalizedExpectedCity}") but got "${returnedCity}" (normalized: "${normalizedReturnedCity}")`);
      return false;
    }

    console.log(`[GEOCODING] ✅ Validation passed: "${returnedCity}, ${returnedState}" matches expected "${expectedCity}, ${expectedState}"`);
    return true;
  } catch (error) {
    console.warn(`[GEOCODING] Validation error:`, error);
    return true; // Assume valid if validation fails
  }
}

/**
 * Get state abbreviation from full name (simple lookup)
 */
function getStateAbbreviation(fullName: string): string {
  const stateMap: Record<string, string> = {
    'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar',
    'california': 'ca', 'colorado': 'co', 'connecticut': 'ct', 'delaware': 'de',
    'florida': 'fl', 'georgia': 'ga', 'hawaii': 'hi', 'idaho': 'id',
    'illinois': 'il', 'indiana': 'in', 'iowa': 'ia', 'kansas': 'ks',
    'kentucky': 'ky', 'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
    'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn', 'mississippi': 'ms',
    'missouri': 'mo', 'montana': 'mt', 'nebraska': 'ne', 'nevada': 'nv',
    'new hampshire': 'nh', 'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny',
    'north carolina': 'nc', 'north dakota': 'nd', 'ohio': 'oh', 'oklahoma': 'ok',
    'oregon': 'or', 'pennsylvania': 'pa', 'rhode island': 'ri', 'south carolina': 'sc',
    'south dakota': 'sd', 'tennessee': 'tn', 'texas': 'tx', 'utah': 'ut',
    'vermont': 'vt', 'virginia': 'va', 'washington': 'wa', 'west virginia': 'wv',
    'wisconsin': 'wi', 'wyoming': 'wy', 'district of columbia': 'dc'
  };
  return stateMap[fullName.toLowerCase()] || fullName.substring(0, 2).toLowerCase();
}

/**
 * Structured geocoding using Maps.co API
 */
async function geocodeStructured(
  address: string,
  city: string,
  state: string,
  zipCode: string | undefined,
  apiKey: string
): Promise<GeocodingResult> {
  const params = new URLSearchParams({
    street: address,
    city: city,
    state: state,
    country: 'US',
    api_key: apiKey
  });

  if (zipCode) {
    params.set('postalcode', zipCode);
  }

  const response = await fetch(`https://geocode.maps.co/search?${params}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Maps.co API error: ${response.status}`);
  }

  const data: MapsCoResponse[] = await response.json();
  
  if (data.length === 0) {
    return { latitude: 0, longitude: 0, accuracy: 'failed', source: 'maps_co' };
  }

  const result = data[0];
  return {
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
    accuracy: 'exact',
    source: 'maps_co'
  };
}

/**
 * Free-form geocoding using Maps.co API
 * Returns the first valid result, or all results if validateCity/State provided
 */
async function geocodeFreeForm(
  searchQuery: string,
  apiKey: string,
  validateCity?: string,
  validateState?: string
): Promise<GeocodingResult> {
  const params = new URLSearchParams({
    q: searchQuery,
    api_key: apiKey
  });

  const response = await fetch(`https://geocode.maps.co/search?${params}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Maps.co API error: ${response.status}`);
  }

  const data: MapsCoResponse[] = await response.json();
  
  if (data.length === 0) {
    return { latitude: 0, longitude: 0, accuracy: 'failed', source: 'maps_co' };
  }

  // If validation is requested, try to find a result that matches
  if (validateCity && validateState) {
    for (const item of data) {
      const result: GeocodingResult = {
        latitude: parseFloat(item.lat),
        longitude: parseFloat(item.lon),
        accuracy: 'city',
        source: 'maps_co'
      };
      
      // Validate this result
      const isValid = await validateGeocodeResult(result, validateCity, validateState);
      if (isValid) {
        console.log(`[GEOCODING] Found validated result in search results (tried ${data.indexOf(item) + 1} of ${data.length})`);
        return result;
      }
    }
    // If no validated result found, return the first one anyway (validation will catch it)
    console.warn(`[GEOCODING] No validated result found in ${data.length} search results`);
  }

  // Return first result
  const result = data[0];
  return {
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
    accuracy: 'city', // Free-form is less accurate
    source: 'maps_co'
  };
}

/**
 * Build search query from address components
 */
function buildSearchQuery(
  address?: string,
  city?: string,
  state?: string,
  zipCode?: string
): string {
  const parts: string[] = [];
  
  if (address) parts.push(address);
  if (city) parts.push(city);
  if (state) parts.push(state);
  if (zipCode) parts.push(zipCode);
  
  return parts.join(', ');
}

/**
 * Get fallback coordinates for city/state
 */
function getFallbackCoordinates(city?: string, state?: string): GeocodingResult {
  // Common city coordinates as fallback
  const cityCoordinates: Record<string, { lat: number; lon: number }> = {
    'Dallas': { lat: 32.7767, lon: -96.7970 },
    'Jacksonville': { lat: 30.3322, lon: -81.6557 },
    'Tallahassee': { lat: 30.4518, lon: -84.2807 },
    'Palmetto': { lat: 27.5211, lon: -82.5723 },
    'Carrabelle': { lat: 29.8538, lon: -84.6646 },
    'Hosford': { lat: 30.3638, lon: -84.4710 },
    'Summerfield': { lat: 29.0089, lon: -82.0351 },
  };

  if (city && cityCoordinates[city]) {
    const coords = cityCoordinates[city];
    return {
      latitude: coords.lat,
      longitude: coords.lon,
      accuracy: 'city',
      source: 'fallback'
    };
  }

  // State-level fallback
  const stateCoordinates: Record<string, { lat: number; lon: number }> = {
    'Florida': { lat: 27.7663, lon: -82.6404 },
    'Texas': { lat: 31.9686, lon: -99.9018 },
    'California': { lat: 36.7783, lon: -119.4179 },
    'New York': { lat: 42.1657, lon: -74.9481 },
  };

  if (state && stateCoordinates[state]) {
    const coords = stateCoordinates[state];
    return {
      latitude: coords.lat,
      longitude: coords.lon,
      accuracy: 'state',
      source: 'fallback'
    };
  }

  // Ultimate fallback: center of US
  return {
    latitude: 39.8283,
    longitude: -98.5795,
    accuracy: 'state',
    source: 'fallback'
  };
}

/**
 * Batch geocode multiple addresses with rate limiting
 */
export async function batchGeocodeAddresses(
  addresses: Array<{
    id: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }>
): Promise<Array<{ id: string; result: GeocodingResult }>> {
  const results: Array<{ id: string; result: GeocodingResult }> = [];

  for (const addr of addresses) {
    try {
      const result = await geocodeAddress(addr.address, addr.city, addr.state, addr.zipCode);
      results.push({ id: addr.id, result });
      
      // Add delay between requests
      await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
    } catch (error) {
      console.warn(`Failed to geocode address for ${addr.id}:`, error);
      results.push({
        id: addr.id,
        result: getFallbackCoordinates(addr.city, addr.state)
      });
    }
  }

  return results;
}
